import {
  Add,
  ExpandMore,
  MoreHoriz,
  Settings,
  SyncAlt,
  Tag,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "@remix-run/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "~/utils/trpc";
import NavigationCollapseUrl from "../../assets/icons/Navigation_Collapse.svg?url";
import { ArchiveConfirmModal } from "../ArchiveConfirmModal";
import { useAuth } from "../AuthProvider";
import { useTeamProject } from "../TeamProjectProvider";
import { useToast } from "../ToastProvider";
import { UserProfile } from "./UserProfile";

import CrosImg from "../../assets/icons/cros-air-logo.svg?url";

const drawerWidth = 280;

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileClose,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { authData } = useAuth();
  const {
    teams,
    selectedTeamId,
    projects,
    selectedProjectId,
    setSelectedProjectId,
    isLoadingProjects,
  } = useTeamProject();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showToast } = useToast();
  const utils = trpc.useUtils();

  const selectedTeam = useMemo(
    () => teams.find((t) => t.id === selectedTeamId) || null,
    [teams, selectedTeamId],
  );

  const [teamMenuAnchor, setTeamMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  // Project archive state
  const [projectMenuAnchor, setProjectMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [projectToArchive, setProjectToArchive] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [archiveProjectConfirmOpen, setArchiveProjectConfirmOpen] =
    useState(false);

  const archiveProjectMutation = trpc.project.archive.useMutation({
    onSuccess: () => {
      utils.project.list.invalidate();
      utils.project.listArchived.invalidate();
      showToast(
        t("project.projectArchived", { name: projectToArchive?.name }),
        "success",
      );
      setArchiveProjectConfirmOpen(false);
      setProjectToArchive(null);
      // Auto-select next project
      const remaining = projects.filter((p) => p.id !== projectToArchive?.id);
      if (remaining.length > 0) {
        setSelectedProjectId(remaining[0].id);
      } else {
        setSelectedProjectId("");
      }
    },
    onError: (err) => showToast(err.message, "error"),
  });

  // Archived teams
  const archivedTeamsQuery = trpc.team.listArchived.useQuery();
  const hasArchivedTeams = (archivedTeamsQuery.data?.length ?? 0) > 0;

  const handleProjectClick = (projectId: string) => {
    setSelectedProjectId(projectId);
    const params = new URLSearchParams(location.search);
    const hadTask = params.has("task");
    if (hadTask) params.delete("task");
    const nextSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (hadTask || nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    }
    if (isMobile && onMobileClose) onMobileClose();
  };

  const handleOpenCreateProject = () => {
    document.dispatchEvent(new CustomEvent("open-create-project-modal"));
    if (isMobile && onMobileClose) onMobileClose();
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.custom.gray[0],
        borderRight: `1px solid ${theme.palette.custom.divider}`,
        py: { xs: 1.5 },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          component="img"
          src={CrosImg}
          alt="CROSAIR"
          sx={{ height: 20, width: "auto" }}
        />

        {isMobile && (
          <IconButton
            size="small"
            onClick={() =>
              document.dispatchEvent(new CustomEvent("close-mobile-sidebar"))
            }
            sx={{
              p: 0,
              color: theme.palette.custom.gray[700],
            }}
          >
            <img src={NavigationCollapseUrl} alt="Collapse navigation" />
          </IconButton>
        )}
      </Box>

      {/* Team Selector */}
      <Box sx={{ py: 0 }}>
        <ListItemButton
          onClick={(e) => setTeamMenuAnchor(e.currentTarget)}
          sx={{
            minHeight: 56,
            borderRadius: 0,
            py: 1.5,
            px: 3,
            bgcolor: teamMenuAnchor
              ? theme.palette.custom.primarySecondaryPressed
              : "transparent",
            "&:hover": { bgcolor: theme.palette.custom.primarySecondaryHover },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: theme.palette.custom.gray[200],
              color: theme.palette.custom.gray[600],
              border: `1px solid ${theme.palette.custom.gray[300]}`,
              mr: 1.5,
            }}
          >
            {selectedTeam?.name?.[0]?.toUpperCase() ?? "T"}
          </Avatar>
          <Typography
            variant="body1"
            sx={{
              flex: 1,
              fontWeight: 500,
              color: theme.palette.custom.gray[700],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedTeam?.name ?? t("nav.selectTeam")}
          </Typography>
          <ExpandMore
            sx={{
              fontSize: 22,
              color: theme.palette.custom.gray[700],
            }}
          />
        </ListItemButton>

        <Menu
          anchorEl={teamMenuAnchor}
          open={Boolean(teamMenuAnchor)}
          onClose={() => setTeamMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                ml: 3,
                minWidth: 164,
                borderRadius: "var(--Input-Radius-Medium)",
                border: `1px solid ${theme.palette.custom.gray[200]}`,
                boxShadow: "0px 4px 8px rgba(0,0,0,0.08)",
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setTeamMenuAnchor(null);
              const params = new URLSearchParams({ from: "switch-team" });
              if (location.pathname && location.pathname !== "/dashboard") {
                params.set("returnTo", location.pathname);
              }
              navigate(`/select-team?${params.toString()}`);
            }}
            sx={{ minHeight: 44, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <SyncAlt
                fontSize="small"
                sx={{ color: theme.palette.custom.gray[700] }}
              />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.custom.gray[700] }}
            >
              {t("nav.switchTeam")}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate("/dashboard/team-settings");
              setTeamMenuAnchor(null);
              onMobileClose?.();
            }}
            sx={{ minHeight: 44, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Settings
                fontSize="small"
                sx={{ color: theme.palette.custom.gray[700] }}
              />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.custom.gray[700] }}
            >
              {t("nav.teamSettings")}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Projects Section */}
      <Box sx={{ px: 3, flex: 1, overflowY: "auto" }}>
        <Box
          sx={{
            py: 1,
            mb: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.custom.gray[600],
              fontWeight: 500,
            }}
          >
            {t("nav.projects")}
          </Typography>
          <IconButton
            size="small"
            onClick={handleOpenCreateProject}
            sx={{ p: 0, color: theme.palette.custom.gray[700] }}
          >
            <Add sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>

        {isLoadingProjects ? (
          <Stack spacing={0}>
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.5,
                  px: 0,
                  minHeight: 52,
                }}
              >
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  sx={{ mr: 1, flexShrink: 0, transform: "none" }}
                />
                <Skeleton
                  variant="text"
                  width={`${50 + i * 12}%`}
                  height={20}
                  sx={{ transform: "none" }}
                />
              </Box>
            ))}
          </Stack>
        ) : (
          <List disablePadding>
            {projects.map((project) => {
              const isActive = project.id === selectedProjectId;
              return (
                <ListItemButton
                  key={project.id}
                  selected={isActive}
                  onClick={() => handleProjectClick(project.id)}
                  sx={{
                    borderRadius: "var(--Input-Radius-Medium)",
                    minHeight: 48,
                    py: 1.25,
                    px: 3,
                    mx: -3,
                    transition: theme.transitions.create(
                      ["background-color", "color"],
                      { duration: theme.transitions.duration.shortest },
                    ),
                    "&.Mui-selected": {
                      bgcolor: theme.palette.custom.primarySecondaryHover,
                      color: theme.palette.custom.primary,
                    },
                    "&:hover": {
                      bgcolor: `${theme.palette.custom.primarySecondaryHover} !important`,
                      color: theme.palette.custom.primary,
                    },
                  }}
                >
                  <Tag
                    sx={{
                      fontSize: 24,
                      mr: 1,
                      color: isActive ? "primary" : "secondary",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      color: isActive
                        ? theme.palette.custom.primary
                        : theme.palette.custom.gray[700],
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor: "transparent !important",
                        color: theme.palette.custom.primary,
                      },
                    }}
                  >
                    {project.name}
                  </Typography>
                  <IconButton
                    size="small"
                    className="project-more-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjectMenuAnchor(e.currentTarget);
                      setProjectToArchive({
                        id: project.id,
                        name: project.name,
                      });
                    }}
                    sx={{
                      p: 0.5,
                      ml: 0.5,
                      flexShrink: 0,
                      color: theme.palette.custom.gray[500],
                      opacity: isActive ? 1 : 0,
                      pointerEvents: isActive ? "auto" : "none",
                      transition: theme.transitions.create(
                        ["opacity", "color"],
                        { duration: theme.transitions.duration.shortest },
                      ),
                      ".MuiListItemButton-root:hover &": {
                        opacity: 1,
                        pointerEvents: "auto",
                        color: theme.palette.custom.gray[700],
                      },
                    }}
                  >
                    <MoreHoriz sx={{ fontSize: 20 }} />
                  </IconButton>
                </ListItemButton>
              );
            })}
          </List>
        )}

        {/* Project context menu */}
        <Menu
          anchorEl={projectMenuAnchor}
          open={Boolean(projectMenuAnchor)}
          onClose={() => {
            setProjectMenuAnchor(null);
            setProjectToArchive(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: 120,
                borderRadius: "var(--Input-Radius-Medium)",
                border: `1px solid ${theme.palette.custom.gray[200]}`,
                boxShadow: "0px 4px 8px rgba(0,0,0,0.08)",
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setProjectMenuAnchor(null);
              setArchiveProjectConfirmOpen(true);
            }}
            sx={{ minHeight: 44, px: 2 }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.palette.custom.destructive }}
            >
              {t("project.archiveProject")}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* User Profile */}
      <UserProfile
        collapsed={false}
        onOpenArchivedTeams={() => navigate("/archived-teams")}
        showArchivedTeams={hasArchivedTeams}
      />

      {/* Archive Project Confirm Modal */}
      <ArchiveConfirmModal
        open={archiveProjectConfirmOpen}
        onClose={() => {
          setArchiveProjectConfirmOpen(false);
          setProjectToArchive(null);
        }}
        onConfirm={() => {
          if (projectToArchive) {
            archiveProjectMutation.mutate({ projectId: projectToArchive.id });
          }
        }}
        confirmPending={archiveProjectMutation.isPending}
        title={t("project.archiveProjectConfirmTitle")}
        description={t("project.archiveProjectConfirmDesc")}
      />
    </Box>
  );

  // Mobile: use temporary Drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  // Desktop: permanent Drawer
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          border: "none",
        },
      }}
      open
    >
      {drawer}
    </Drawer>
  );
};
